/* ==================================================================
   scrollEngine
   ------------------------------------------------------------------
   ONE passive scroll listener and ONE requestAnimationFrame loop for
   the whole page, no matter how many sections animate on scroll.

   Every subscriber is a { measure, apply } pair and the engine runs
   all measures before any apply, so a frame never interleaves layout
   reads with style writes (no forced synchronous reflow per section).
   Subscribers write CSS custom properties only, which keeps scroll
   work off the React render path entirely.
   ================================================================== */

const subscribers = new Set();
let frameId = 0;
let listening = false;
const results = [];

function runFrame() {
  frameId = 0;
  const viewport = window.innerHeight || 1;

  /* Phase 1 - read. */
  results.length = 0;
  subscribers.forEach((sub) => {
    results.push([sub, sub.measure ? sub.measure(viewport) : null]);
  });

  /* Phase 2 - write. */
  for (let i = 0; i < results.length; i += 1) {
    const [sub, measured] = results[i];
    sub.apply(measured, viewport);
  }
  results.length = 0;
}

function schedule() {
  if (!frameId) frameId = requestAnimationFrame(runFrame);
}

function startListening() {
  if (listening) return;
  listening = true;
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule, { passive: true });
}

function stopListening() {
  if (!listening) return;
  listening = false;
  window.removeEventListener('scroll', schedule);
  window.removeEventListener('resize', schedule);
  window.removeEventListener('orientationchange', schedule);
  if (frameId) {
    cancelAnimationFrame(frameId);
    frameId = 0;
  }
}

/** Register a { measure, apply } subscriber. Returns an unsubscribe fn. */
export function subscribeToScroll(subscriber) {
  subscribers.add(subscriber);
  startListening();
  schedule();
  return () => {
    subscribers.delete(subscriber);
    if (subscribers.size === 0) stopListening();
  };
}

/** Force a recompute (used after fonts/images shift the layout). */
export function refreshScroll() {
  if (listening) schedule();
}

export const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);
